import { DatabaseIcon, ExternalLinkIcon, FilterIcon, LinkIcon } from "@heroicons/react/outline";
import { useCallback, useMemo, type MouseEvent as ReactMouseEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { buildRequestNavigationSearch } from "../utils/buildRequestNavigationSearch";

import type { RequestRow } from "./requestLogGridTypes";
import { useRecentlyViewedRequestLogSession } from "./useRecentlyViewedRequestLogSession";

import { useStructuredSearchStore } from "@/components/StructuredSearch/stores";
import { useStructuredSearchGridPaginationSort } from "@/components/StructuredSearch/hooks/useStructuredSearchGridPaginationSort";
import { useUser } from "@/context/user-context";
import {
  getStructuredQueryTrackingProperties,
  trackRequestsAnalyticsEvent,
} from "@/utils/requests-analytics-tracking";
const isPlaceholderRow = (row: RequestRow) =>
  !row.id || String(row.id).startsWith("__placeholder-");

const canAddPromptFilter = (row: RequestRow) =>
  !isPlaceholderRow(row) &&
  typeof row.prompt_id === "number" &&
  typeof row.prompt_version_number === "number";

type RequestLogsGridTrackingSurface = "requests_analytics";

export const useRequestLogsGridInteractions = (
  trackingSurface?: RequestLogsGridTrackingSurface,
  onAddToDataset?: (row: RequestRow) => void,
) => {
  const structuredSearchStore = useStructuredSearchStore();
  const workspaceId = useUser()?.activeWorkspaceId ?? "";
  const navigate = useNavigate();
  const location = useLocation();
  const { recentlyViewedRequestId, markRecentlyViewedRequest } =
    useRecentlyViewedRequestLogSession();

  const getTrackingProperties = useCallback(
    () =>
      getStructuredQueryTrackingProperties({
        filters: structuredSearchStore.filters,
        q: structuredSearchStore.q,
        dateRange: structuredSearchStore.dateRange,
      }),
    [structuredSearchStore.dateRange, structuredSearchStore.filters, structuredSearchStore.q],
  );

  const { currentPage, pageSize, handlePaginationChanged, handleSortChanged } =
    useStructuredSearchGridPaginationSort({
      onPageSizeChange: (newPageSize, previousPageSize) => {
        if (!trackingSurface) {
          return;
        }
        trackRequestsAnalyticsEvent("Requests Grid Page Size Changed", {
          page: "Requests & Analytics",
          surface: trackingSurface,
          page_size: newPageSize,
          previous_page_size: previousPageSize,
          ...getTrackingProperties(),
        });
      },
      onPageIndexChange: (newPageIndex, previousPageIndex) => {
        if (!trackingSurface) {
          return;
        }
        trackRequestsAnalyticsEvent("Requests Grid Page Changed", {
          page: "Requests & Analytics",
          surface: trackingSurface,
          page_index: newPageIndex,
          previous_page_index: previousPageIndex,
          page_size: structuredSearchStore.pageSize,
          ...getTrackingProperties(),
        });
      },
      onSortChange: (sortField, sortDirection) => {
        if (!trackingSurface) {
          return;
        }
        if (!sortField) {
          if (structuredSearchStore.sorting.length > 0) {
            trackRequestsAnalyticsEvent("Requests Grid Sort Cleared", {
              page: "Requests & Analytics",
              surface: trackingSurface,
              ...getTrackingProperties(),
            });
          }
          return;
        }
        trackRequestsAnalyticsEvent("Requests Grid Sorted", {
          page: "Requests & Analytics",
          surface: trackingSurface,
          sort_field: sortField,
          sort_direction: sortDirection ?? "none",
          ...getTrackingProperties(),
        });
      },
    });

  const buildRequestNavigationSearchForLocation = useCallback(
    (): string =>
      buildRequestNavigationSearch({
        pathname: location.pathname,
        locationSearch: location.search,
        structuredSearchStore,
        trackingSurface,
      }),
    [location.pathname, location.search, structuredSearchStore, trackingSurface],
  );

  const getRowHref = useCallback(
    (row: RequestRow): string | undefined => {
      if (isPlaceholderRow(row)) {
        return undefined;
      }
      const search = buildRequestNavigationSearchForLocation();
      return `/workspace/${workspaceId}/request/${row.id}${search}`;
    },
    [buildRequestNavigationSearchForLocation, workspaceId],
  );

  const openRequestInNewTab = useCallback(
    (requestId: RequestRow["id"]) => {
      markRecentlyViewedRequest(requestId);
      const search = buildRequestNavigationSearchForLocation();
      window.open(
        `/workspace/${workspaceId}/request/${requestId}${search}`,
        "_blank",
        "noopener,noreferrer",
      );
    },
    [buildRequestNavigationSearchForLocation, markRecentlyViewedRequest, workspaceId],
  );

  const openTraceInNewTab = useCallback(
    (traceId: string) => {
      window.open(`/workspace/${workspaceId}/traces/${traceId}`, "_blank", "noopener,noreferrer");
    },
    [workspaceId],
  );

  const handleRowClick = useCallback(
    (row: RequestRow, event?: MouseEvent) => {
      if (isPlaceholderRow(row)) {
        return;
      }

      // Clicks anywhere in the select column belong to the checkbox UI, not
      // navigation — including the cell's padding area around the checkbox.
      const target = event?.target as HTMLElement | null | undefined;
      const cellColId = target?.closest("[col-id]")?.getAttribute("col-id");
      if (cellColId === "col_-1") {
        return;
      }

      // Shift is reserved for range-select interactions; never navigate.
      if (event?.shiftKey) {
        return;
      }

      const requestSearch = buildRequestNavigationSearchForLocation();
      const requestUrl = `/workspace/${workspaceId}/request/${row.id}${requestSearch}`;
      if (event?.metaKey || event?.ctrlKey) {
        if (trackingSurface) {
          trackRequestsAnalyticsEvent("Request Opened From Grid", {
            page: "Requests & Analytics",
            surface: trackingSurface,
            open_target: "new_tab",
          });
        }
        openRequestInNewTab(row.id);
        return;
      }

      markRecentlyViewedRequest(row.id);

      if (trackingSurface) {
        trackRequestsAnalyticsEvent("Request Opened From Grid", {
          page: "Requests & Analytics",
          surface: trackingSurface,
          open_target: "same_tab",
        });
      }
      navigate(requestUrl);
    },
    [
      buildRequestNavigationSearchForLocation,
      markRecentlyViewedRequest,
      navigate,
      openRequestInNewTab,
      trackingSurface,
      workspaceId,
    ],
  );

  const handleRowLinkClick = useCallback(
    (row: RequestRow, event: ReactMouseEvent<HTMLAnchorElement>) => {
      if (event.shiftKey) {
        event.preventDefault();
        return;
      }
      if (!event.metaKey && !event.ctrlKey) {
        event.preventDefault();
        handleRowClick(row, event.nativeEvent);
      }
    },
    [handleRowClick],
  );

  const contextMenuItems = useMemo(
    () => [
      {
        id: "add-to-dataset",
        label: "Add to dataset",
        icon: DatabaseIcon,
        showIf: (row: RequestRow) => !isPlaceholderRow(row) && Number.isFinite(Number(row.id)),
        onClick: (row?: RequestRow) => {
          if (!row || isPlaceholderRow(row) || !Number.isFinite(Number(row.id))) {
            return;
          }
          onAddToDataset?.(row);
        },
      },
      {
        id: "add-filter-by-prompt",
        label: "Add filter by prompt",
        icon: FilterIcon,
        showIf: canAddPromptFilter,
        onClick: (row?: RequestRow) => {
          if (
            !row ||
            isPlaceholderRow(row) ||
            typeof row.prompt_id !== "number" ||
            typeof row.prompt_version_number !== "number"
          ) {
            return;
          }

          if (trackingSurface) {
            trackRequestsAnalyticsEvent("Requests Grid Prompt Filter Added From Row", {
              page: "Requests & Analytics",
              surface: trackingSurface,
              has_prompt_version: typeof row.prompt_version_number === "number",
            });
          }

          structuredSearchStore.addPromptFilter(
            row.prompt_id,
            row.prompt_version_number,
            row.prompt_name ?? undefined,
          );
        },
      },
      {
        id: "divider",
        isDivider: true,
        showIf: canAddPromptFilter,
      },
      {
        id: "open-trace",
        label: "Open trace",
        icon: LinkIcon,
        showIf: (row: RequestRow) =>
          !isPlaceholderRow(row) && typeof row.trace_id === "string" && row.trace_id.length > 0,
        onClick: (row?: RequestRow) => {
          if (!row || isPlaceholderRow(row) || !row.trace_id) {
            return;
          }

          if (trackingSurface) {
            trackRequestsAnalyticsEvent("Trace Opened From Requests Grid", {
              page: "Requests & Analytics",
              surface: trackingSurface,
            });
          }
          openTraceInNewTab(row.trace_id);
        },
      },
      {
        id: "open-in-new-tab",
        label: "Open in new tab",
        icon: ExternalLinkIcon,
        onClick: (row?: RequestRow) => {
          if (!row || isPlaceholderRow(row)) {
            return;
          }

          if (trackingSurface) {
            trackRequestsAnalyticsEvent("Request Opened From Grid", {
              page: "Requests & Analytics",
              surface: trackingSurface,
              open_target: "new_tab_context_menu",
            });
          }
          openRequestInNewTab(row.id);
        },
      },
    ],
    [
      onAddToDataset,
      openRequestInNewTab,
      openTraceInNewTab,
      structuredSearchStore,
      trackingSurface,
    ],
  );

  return {
    contextMenuItems,
    currentPage,
    getRowHref,
    handlePaginationChanged,
    handleRowClick,
    handleRowLinkClick,
    handleSortChanged,
    pageSize,
    recentlyViewedRequestId,
  };
};
